import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { startCase } from 'lodash-es';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { numberToIntegerOrDecimal } from '~utils/utilFunctions';

import { VendorData } from '../types';
import { VendorTicketsTableColumn } from '../vendor-tickets-table-columns';

interface VendorRowProps {
  vendor: VendorData;
  onToggleExpanded: (id: string) => void;
  isExpanded: boolean;
  columns: VendorTicketsTableColumn[];
}

const VendorRow = ({ vendor, onToggleExpanded, isExpanded, columns }: VendorRowProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'grid',
        gridAutoRows: '50px',
        gridColumn: `span ${columns.length}`,
        gridTemplateColumns: columns.map((column) => column.size).join(' '),
        overflow: 'hidden',
        '&:hover': {
          bgcolor: theme.palette.action.hover,
        },
      }}
    >
      <Box sx={{ ...columns[0].sx, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={() => onToggleExpanded(vendor.name)}
          sx={{
            padding: '2px',
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
            },
          }}
          data-ui="page-dispatch__vendor__button"
        >
          {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <OverflowAwareText>{vendor.name}</OverflowAwareText>
      </Box>
      <Box sx={{ ...columns[1].sx, display: 'flex', alignItems: 'center' }}>
        <OverflowAwareText>{vendor.ticketCount}</OverflowAwareText>
      </Box>
      <Box sx={{ ...columns[2].sx, display: 'flex', alignItems: 'center' }}>
        <OverflowAwareText>
          {numberToIntegerOrDecimal(vendor.totalQuantity)}{' '}
          {startCase(vendor.unitOfMeasure)}
        </OverflowAwareText>
      </Box>
      <Box
        sx={{
          ...columns[3].sx,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          overflow: 'hidden',
          minWidth: 0,
          '& .MuiChip-root': {
            maxWidth: '100%',
          },
        }}
      >
        <Chip
          label={vendor.matchedTruckCount}
          size="small"
          sx={{
            backgroundColor: theme.palette.success.main,
            color: 'white',
            minWidth: 24,
            height: 24,
            flexShrink: 0,
          }}
        />
        <Chip
          label={vendor.unmatchedTruckCount}
          size="small"
          sx={{
            backgroundColor: theme.palette.error.main,
            color: 'white',
            minWidth: 24,
            height: 24,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
};

export default VendorRow;
